import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Container, Alert, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { BASE_URL } from '../../Constants/constants';

import './PasswordReset.css';

const PasswordReset = () => {
    const { t } = useTranslation('userManagement');
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: email, 2: code, 3: password
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Step 1: Validate email and send verification code
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            // First validate if email exists
            const validateResponse = await axios.post(`${BASE_URL}/validate-email`, { email });

            if (!validateResponse.data.exists) {
                setError(t('messages.emailNotExist'));
                setIsSubmitting(false);
                return;
            }

            // Send verification code
            const codeResponse = await axios.post(`${BASE_URL}/send-verification-code`, { email });

            if (codeResponse.status === 200) {
                setSuccess(t('messages.emailSent'));
                setStep(2);
            }
        } catch (err) {
            setError(err.response?.data?.detail || t('messages.error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Step 2: Verify code
    const handleCodeSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            const response = await axios.post(`${BASE_URL}/verify-code`, {
                email,
                code
            });

            if (response.data.valid) {
                setSuccess(t('messages.codeVerified'));
                setStep(3);
            }
        } catch (err) {
            setError(err.response?.data?.detail || t('messages.invalidCode'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Step 3: Reset password
    const validatePasswords = () => {
        if (password !== passwordConfirm) {
            setError(t('messages.passwordMismatch'));
            return false;
        }
        if (!password.match(/^(?=.*[a-zA-Z])(?=.*[0-9]).{8,}$/)) {
            setError(t('messages.passwordRequirements'));
            return false;
        }
        return true;
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!validatePasswords()) return;

        setIsSubmitting(true);
        setError('');

        try {
            const response = await axios.post(`${BASE_URL}/reset-password`, {
                email,
                code,
                password
            });

            if (response.status === 200) {
                setSuccess(t('messages.passwordResetSuccess'));
                setTimeout(() => {
                    navigate("/login");
                }, 2000);
            }
        } catch (err) {
            setError(err.response?.data?.detail || t('messages.error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendCode = async () => {
        setIsSubmitting(true);
        setError('');

        try {
            await axios.post(`${BASE_URL}/send-verification-code`, { email });
            setSuccess(t('messages.codeResent'));
        } catch (err) {
            setError(t('messages.error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container className="d-flex align-items-center justify-content-center custom-container">
            <Card className="shadow-sm custom-card">
                <Card.Body>
                    <Card.Title className="text-center mb-4">
                        {t('passwordReset.title')}
                    </Card.Title>
                    {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
                    {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

                    {/* Step 1: Email Entry */}
                    {step === 1 && (
                        <>
                            <p className="text-muted">
                                {t('passwordReset.step1Description')}
                            </p>
                            <Form onSubmit={handleEmailSubmit}>
                                <Form.Group className="mb-3" controlId="formBasicEmail">
                                    <Form.Label>{t('form.emailAddress')}</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder={t('passwordReset.enterEmail')}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="w-100"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? t('passwordReset.sending') : t('buttons.sendCode')}
                                </Button>
                            </Form>
                        </>
                    )}

                    {/* Step 2: Code Verification */}
                    {step === 2 && (
                        <>
                            <p className="text-muted">
                                {t('passwordReset.step2Description')} <strong>{email}</strong>.
                                {' '}{t('passwordReset.step2ExpiryNote')}
                            </p>
                            <Form onSubmit={handleCodeSubmit}>
                                <Form.Group className="mb-3" controlId="formVerificationCode">
                                    <Form.Label>{t('form.verificationCode')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('passwordReset.enterCode')}
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        maxLength={6}
                                        required
                                        disabled={isSubmitting}
                                        style={{ letterSpacing: '0.5em', fontSize: '1.2em', textAlign: 'center' }}
                                    />
                                </Form.Group>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="w-100 mb-2"
                                    disabled={isSubmitting || code.length !== 6}
                                >
                                    {isSubmitting ? t('passwordReset.verifying') : t('buttons.verifyCode')}
                                </Button>
                                <Button
                                    variant="link"
                                    onClick={handleResendCode}
                                    className="w-100"
                                    disabled={isSubmitting}
                                >
                                    {t('passwordReset.didNotReceive')}
                                </Button>
                                <Button
                                    variant="link"
                                    onClick={() => {
                                        setStep(1);
                                        setCode('');
                                        setError('');
                                        setSuccess('');
                                    }}
                                    className="w-100 text-muted"
                                    size="sm"
                                >
                                    {t('passwordReset.changeEmail')}
                                </Button>
                            </Form>
                        </>
                    )}

                    {/* Step 3: Password Reset */}
                    {step === 3 && (
                        <>
                            <p className="text-muted">
                                {t('passwordReset.step3Description')}
                            </p>
                            <Form onSubmit={handlePasswordSubmit}>
                                <Form.Group className="mb-3" controlId="formBasicPassword">
                                    <Form.Label>{t('form.newPassword')}</Form.Label>
                                    <Form.Control
                                        type="password"
                                        placeholder={t('passwordReset.enterNewPassword')}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="formBasicPasswordConfirm">
                                    <Form.Label>{t('form.confirmNewPassword')}</Form.Label>
                                    <Form.Control
                                        type="password"
                                        placeholder={t('passwordReset.confirmNewPassword')}
                                        value={passwordConfirm}
                                        onChange={(e) => setPasswordConfirm(e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="w-100"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? t('passwordReset.resetting') : t('buttons.resetPassword')}
                                </Button>
                            </Form>
                        </>
                    )}

                    <div className="text-center mt-3">
                        <Button
                            variant="link"
                            onClick={() => navigate("/login")}
                            disabled={isSubmitting}
                        >
                            {t('passwordReset.backToLogin')}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default PasswordReset;